import {
  Breakpoints,
  Button,
  ButtonColor,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import groq from 'groq';
import Link from 'next/link';
import { type ComponentPropsWithoutRef, type FC } from 'react';

import { initializeApollo } from '../../lib/apolloClient';
import { type ArticleWithWrittenAt } from '../../lib/sanity/commonGroqs';
import { sanityClient } from '../../lib/sanity/sanityClient';
import { DEFAULT_PAGE_SIZE } from '../../utils/pagination';
import { getLibraryPageUrl } from '../../utils/routing';
import { FEATURED_TAGS } from '../../utils/tags';
import Pagination from '../common/Pagination';

import ArticleCard, { ARTICLE_CARD_ARTICLE_PROJECTION } from './ArticleCard';
import LibraryNav, {
  LIBRARY_NAV_CATEGORY_QUERY,
  type LibraryNavCategory,
  type LibraryNavGoal,
  type LibraryNavGoalSection,
} from './LibraryNav';
import {
  LibraryPageCategoriesDocument,
  type LibraryPageCategoriesQuery,
  type LibraryPageCategoriesQueryVariables,
} from './__generated-gql-types__/LibraryPage.generated';
export { NonCategorySlugs } from './LibraryNav';

export const LIBRARY_PAGE_ARTICLE_PROJECTION: string =
  ARTICLE_CARD_ARTICLE_PROJECTION;

const BODY_PADDING = 'w-11/12 max-w-1500 mx-auto';

type Props = ComponentPropsWithoutRef<typeof LibraryNav> & {
  title: string;
  subtitle: string;
  featuredArticles?: ArticleWithWrittenAt[];
  recentArticles: ArticleWithWrittenAt[];
  totalArticles: number;
  page: number;
};

const LibraryPage: FC<Props> = ({
  title,
  subtitle,
  featuredArticles,
  recentArticles,
  totalArticles,
  page,
  ...navProps
}) => {
  const isSm = useMediaQuery(Breakpoints.SM);

  return (
    <div className={BODY_PADDING}>
      <div className="h-48 py-12">
        <h1 className="mb-6 text-4xl font-semibold lg:text-5xl">{title}</h1>
        <p className="w-full text-lg text-leland-gray-light lg:w-1/2 lg:text-xl">
          {subtitle.slice(0, 150)}
        </p>
      </div>

      <LibraryNav {...navProps} />
      <hr />

      <div className="grid grid-cols-1 gap-8 pt-12 md:grid-cols-12">
        {featuredArticles?.map((article, idx) => (
          <ArticleCard
            key={article._id}
            priority
            className={`mb-3 ${idx > 2 ? 'md:col-span-3' : 'md:col-span-4'}`}
            article={article}
          />
        ))}
      </div>

      <div className="py-12">
        <h2 className="mb-6 text-3xl font-medium">Recent articles</h2>
        {recentArticles.length > 0 ? (
          recentArticles.map((article, idx) => (
            <ArticleCard
              key={article._id}
              article={article}
              className="mb-8 transition-opacity lg:hover:opacity-70"
              priority={!featuredArticles?.length && idx <= 2}
              compact
            />
          ))
        ) : (
          <p>No recent articles.</p>
        )}

        <Pagination
          currentPage={page}
          totalElements={totalArticles}
          pageSize={DEFAULT_PAGE_SIZE}
          buildUrl={(page) => {
            return getLibraryPageUrl({ slug: navProps.activeSlug, page });
          }}
          maxPageItems={isSm ? 5 : 3}
          useShallowRouting={false}
        />
        {navProps.activeSlug ? (
          <>
            <h3 className="mt-8 text-3xl font-medium">Browse by tag</h3>
            <div className="mt-6 flex w-full flex-wrap space-x-1.5">
              {FEATURED_TAGS.map((tag) => (
                <Link
                  key={tag._id}
                  href={getLibraryPageUrl({
                    slug: navProps.activeSlug,
                    tagSlug: tag.slug.current,
                  })}
                  id="library_page--view_tag"
                >
                  <Button
                    label={tag.label}
                    buttonColor={ButtonColor.WHITE}
                    rounded
                  />
                </Link>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

interface GetStaticPropsParams {
  featuredArticlesQuery: string;
  recentArticlesQuery: string;
  totalArticlesQuery: string;
}
type BaseLibraryPageStaticProps = Pick<
  Props,
  'featuredArticles' | 'recentArticles' | 'totalArticles'
>;
export const getLibraryPageStaticProps = async (
  {
    featuredArticlesQuery,
    recentArticlesQuery,
    totalArticlesQuery,
  }: GetStaticPropsParams,
  params: Record<string, string | number> = {},
): Promise<BaseLibraryPageStaticProps & Pick<Props, 'categoriesByGoal'>> => {
  const { categories, ...rest } = await sanityClient.fetch<
    BaseLibraryPageStaticProps & { categories: LibraryNavCategory[] }
  >(
    groq`{
      ${
        (params.start as number) > 0
          ? ''
          : `"featuredArticles": ${featuredArticlesQuery} [0...7],`
      }
      "recentArticles": ${recentArticlesQuery},
      "totalArticles": ${totalArticlesQuery},
      "categories": ${LIBRARY_NAV_CATEGORY_QUERY},
    }`,
    {
      start: 0,
      end: DEFAULT_PAGE_SIZE,
      ...params,
    },
  );
  const apolloClient = initializeApollo();

  type SanityCategoryMap = Record<string, LibraryNavCategory>;
  interface SanityGoalMap {
    goal: LibraryNavGoal;
    categories: SanityCategoryMap;
  }

  const sanityCategoriesByGoal = categories.reduce<
    Record<string, SanityGoalMap>
  >((map, category) => {
    if (!category.goal || !category.slug) {
      return map;
    }

    if (!map[category.goal.slug.current]) {
      map[category.goal.slug.current] = {
        goal: category.goal,
        categories: {},
      };
    }

    map[category.goal.slug.current].categories[category.slug.current] = {
      ...category,
    };

    return map;
  }, {});

  const { data: categoriesData } = await apolloClient.query<
    LibraryPageCategoriesQuery,
    LibraryPageCategoriesQueryVariables
  >({
    query: LibraryPageCategoriesDocument,
  });

  // The data from the backend is sorted by goal, then category, so we rely on that ordering below
  if (categoriesData) {
    const categoriesByGoal: Map<string, LibraryNavGoalSection> =
      categoriesData.getAllCategories.reduce((cbg, category) => {
        if (!sanityCategoriesByGoal[category.goal]) return cbg;
        if (!cbg.get(category.goal)) {
          cbg.set(category.goal, {
            goal: sanityCategoriesByGoal[category.goal]?.goal,
            categories: [],
          });
        }
        if (sanityCategoriesByGoal[category.goal]?.categories[category.slug]) {
          cbg
            .get(category.goal)
            ?.categories.push(
              sanityCategoriesByGoal[category.goal].categories[category.slug],
            );
        }
        return cbg;
      }, new Map<string, LibraryNavGoalSection>());
    return {
      ...rest,
      categoriesByGoal: Array.from(categoriesByGoal.values()),
    };
  } else {
    return {
      ...rest,
      categoriesByGoal: Object.entries(sanityCategoriesByGoal).map(
        ([, { goal, categories }]) => ({
          goal,
          categories: Object.values(categories),
        }),
      ),
    };
  }
};

export default LibraryPage;
